import { describeMutationError } from "@api/mutationErrors";
import { useMintPairingCode } from "@connector/connectorQueries";
import { useCallback, useEffect, useState } from "react";

interface UsePairingCodeResult {
  code: string | null;
  secondsLeft: number;
  loading: boolean;
  error: string | null;
  expired: boolean;
  generate: (name?: string) => void;
}

export const usePairingCode = (): UsePairingCodeResult => {
  const { mutate, isPending, isError, error } = useMintPairingCode();
  const [code, setCode] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [expiresAt, setExpiresAt] = useState<number | null>(null);

  const expired = code !== null && secondsLeft <= 0;

  // Recomputed from the wall-clock deadline so a backgrounded tab can't drift.
  useEffect(() => {
    if (expiresAt === null) return;
    const id = setInterval(() => {
      const remaining = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining <= 0) clearInterval(id);
    }, 1000);
    return () => clearInterval(id);
  }, [expiresAt]);

  const generate = useCallback(
    (name?: string) => {
      mutate(name, {
        onSuccess: ({ code: next, ttlSeconds }) => {
          setCode(next);
          setSecondsLeft(ttlSeconds);
          setExpiresAt(Date.now() + ttlSeconds * 1000);
        },
        onError: () => {
          setCode(null);
          setExpiresAt(null);
          setSecondsLeft(0);
        },
      });
    },
    [mutate],
  );

  return {
    code,
    secondsLeft,
    loading: isPending,
    error: isError ? describeMutationError(error, "generate a code") : null,
    expired,
    generate,
  };
};
